import { useCallback, useRef, useState } from 'react'
import type { RecordResponseRequest, RecordResponseResult } from '@assessments/shared'
import { isQuestionHidden, isSessionExpired } from '../../services/apiError.js'
import { isIndustryUnavailable, recordResponse } from './api.js'

/**
 * Save-state only. It never carries validation state — the
 * indicator must not signal validation problems, and a failure here means the
 * answer could not be persisted, not that it was invalid.
 */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'unavailable'

export interface UseResponsesAutosave {
  /** Per-question save state, keyed by question id; absent ids are `idle`. */
  status: Record<string, SaveState>
  /** Autosave one question's answer. Coalesces rapid saves per question. */
  save: (questionId: string, body: RecordResponseRequest) => void
  /** Retry a question's last answer after a save-state error. */
  retry: (questionId: string) => void
  /** Mark a question edited-but-not-yet-saved (its latest value is not persisted). */
  invalidate: (questionId: string) => void
  /** True when the question's latest value is already persisted and unchanged since. */
  isClean: (questionId: string) => boolean
  /** Per question, the last body whose save succeeded with nothing newer pending. */
  confirmed: Record<string, RecordResponseRequest>
  /** Record a write the caller awaited itself. */
  markSaved: (questionId: string, body: RecordResponseRequest) => void
  /**
   * Resolves once no autosave is in flight. A caller that writes answers itself must
   * await this first, or an autosave started earlier can land after — and overwrite —
   * the newer value it just wrote.
   */
  flush: () => Promise<void>
}

export interface AutosaveCallbacks {
  /** Every successful write, including ones a newer pending change supersedes. */
  onSaved?: (result: RecordResponseResult) => void
  /** The server says skip logic hides the question, so this view is out of date. */
  onQuestionHidden?: () => void
  /** The server refused a retired industry, so this question's choices are out of date. */
  onIndustryUnavailable?: (questionId: string) => void
}

/**
 * Per-question autosave for a section's answers. Each question saves
 * independently: while one question's write is in flight, only its newest change is
 * kept and sent once the write settles (coalescing), so rapid edits can't race or
 * stack up. `isClean` reports whether a question's current value is already persisted
 * so the caller (Mark complete) can skip re-sending what autosave already
 * stored, while still persisting anything autosave hasn't (untouched prefilled answers).
 *
 * A 401 is routed to `onSessionExpired` rather than the save state: an expired session
 * is not a retryable save failure, and reporting one would leave the editor apparently
 * editable until some later request happened to fail.
 *
 * A retired industry (`unavailable`) is not retryable either: the same body would be
 * refused again, so the caller is told to refresh the choices instead.
 */
export function useResponsesAutosave(
  organizationSurveyId: string,
  onSessionExpired?: () => void,
  callbacks: AutosaveCallbacks = {},
): UseResponsesAutosave {
  const [status, setStatus] = useState<Record<string, SaveState>>({})
  const [confirmed, setConfirmed] = useState<Record<string, RecordResponseRequest>>({})

  // Held in a ref so a caller passing an inline callback doesn't re-create `run`.
  const onSessionExpiredRef = useRef(onSessionExpired)
  onSessionExpiredRef.current = onSessionExpired
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks
  const inFlight = useRef<Record<string, boolean>>({})
  const pending = useRef<Record<string, RecordResponseRequest | null>>({})
  const lastBody = useRef<Record<string, RecordResponseRequest>>({})
  // Questions whose latest requested save has succeeded with nothing newer pending.
  const clean = useRef<Set<string>>(new Set())
  // Saves requested and not yet settled — a coalesced follow-up keeps its predecessor's
  // count, so this only reaches zero once nothing is left to write.
  const activeSaves = useRef(0)
  const idleWaiters = useRef<Array<() => void>>([])

  const releaseIdleWaiters = useCallback(() => {
    if (activeSaves.current > 0) return
    const waiters = idleWaiters.current
    idleWaiters.current = []
    for (const resolve of waiters) resolve()
  }, [])

  const run = useCallback(
    (questionId: string, body: RecordResponseRequest) => {
      lastBody.current[questionId] = body
      clean.current.delete(questionId)
      if (inFlight.current[questionId]) {
        pending.current[questionId] = body
        return
      }
      inFlight.current[questionId] = true
      activeSaves.current += 1
      setStatus((s) => ({ ...s, [questionId]: 'saving' }))
      void (async () => {
        try {
          const result = await recordResponse(organizationSurveyId, questionId, body)
          callbacksRef.current.onSaved?.(result)
          if (pending.current[questionId] == null) {
            clean.current.add(questionId)
            setConfirmed((c) => ({ ...c, [questionId]: body }))
            setStatus((s) => ({ ...s, [questionId]: 'saved' }))
          }
        } catch (err) {
          if (isSessionExpired(err)) {
            onSessionExpiredRef.current?.()
          } else if (isQuestionHidden(err)) {
            setStatus((s) => ({ ...s, [questionId]: 'idle' }))
            callbacksRef.current.onQuestionHidden?.()
          } else if (isIndustryUnavailable(err)) {
            if (pending.current[questionId] == null) {
              setStatus((s) => ({ ...s, [questionId]: 'unavailable' }))
              callbacksRef.current.onIndustryUnavailable?.(questionId)
            }
          } else if (pending.current[questionId] == null) {
            setStatus((s) => ({ ...s, [questionId]: 'error' }))
          }
        } finally {
          inFlight.current[questionId] = false
          const next = pending.current[questionId]
          if (next != null) {
            pending.current[questionId] = null
            run(questionId, next)
          }
          activeSaves.current -= 1
          releaseIdleWaiters()
        }
      })()
    },
    [organizationSurveyId, releaseIdleWaiters],
  )

  const save = useCallback(
    (questionId: string, body: RecordResponseRequest) => {
      run(questionId, body)
    },
    [run],
  )

  const retry = useCallback(
    (questionId: string) => {
      const body = lastBody.current[questionId]
      if (body) run(questionId, body)
    },
    [run],
  )

  const invalidate = useCallback((questionId: string) => {
    clean.current.delete(questionId)
  }, [])

  const isClean = useCallback((questionId: string) => clean.current.has(questionId), [])

  const markSaved = useCallback((questionId: string, body: RecordResponseRequest) => {
    lastBody.current[questionId] = body
    clean.current.add(questionId)
    setConfirmed((c) => ({ ...c, [questionId]: body }))
    // The value is stored, so any earlier failure no longer applies — leaving it would
    // offer a Retry for a save that has since succeeded.
    setStatus((s) => ({ ...s, [questionId]: 'saved' }))
  }, [])

  const flush = useCallback(
    () =>
      activeSaves.current === 0
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            idleWaiters.current.push(resolve)
          }),
    [],
  )

  return { status, save, retry, invalidate, isClean, confirmed, markSaved, flush }
}
